'use client'

import { useState, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { SectionWrapper } from '@/components/SectionWrapper'
import FadeIn from '@/components/ui/FadeIn'
import DetailedProjectEntry from '@/components/DetailedProjectEntry'

import type { Media as MediaType } from '@/payload-types'

interface Project {
  id: string
  title: string
  shortDescription?: string
  projectDate?: string
  place?: string
  products?: Array<{ title: string } | string>
  featuredImage?: MediaType | { url: string } | string
  slug: string
}

interface FilterButtonProps {
  label: string
  isActive: boolean
  onClick: () => void
}

function FilterButton({ label, isActive, onClick }: FilterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        'px-3 py-2 font-ui uppercase border transition-all duration-200',
        isActive
          ? 'bg-brand text-text-white border-brand font-semibold shadow-sm'
          : 'bg-transparent text-text-brand border-border-brand/40 hover:border-border-brand hover:bg-brand/10',
      )}
    >
      {label}
    </button>
  )
}

interface DetailedProjectsOverviewClientProps {
  projects: Project[]
  products: string[]
  showFilters?: boolean
}

export default function DetailedProjectsOverviewClient({
  projects,
  products,
  showFilters = true,
}: DetailedProjectsOverviewClientProps) {
  const [activeFilter, setActiveFilter] = useState('Alle')

  const allProducts = products.includes('Alle') ? products : ['Alle', ...products]

  const filteredProjects = useMemo(
    () =>
      activeFilter === 'Alle'
        ? projects
        : projects.filter((project) =>
            project.products?.some((p) =>
              typeof p === 'string' ? p === activeFilter : p.title === activeFilter,
            ),
          ),
    [activeFilter, projects],
  )

  return (
    <div>
      {showFilters && (
        <div className="container mx-auto px-8 py-8">
          {/* Desktop: button row */}
          <div className="hidden lg:flex flex-wrap gap-4">
            {allProducts.map((product) => (
              <FilterButton
                key={product}
                label={product}
                isActive={activeFilter === product}
                onClick={() => setActiveFilter(product)}
              />
            ))}
          </div>

          {/* Mobile & Tablet: native select */}
          <div className="lg:hidden relative">
            <label htmlFor="detailed-projects-filter" className="sr-only">
              Kategorie filtern
            </label>
            <select
              id="detailed-projects-filter"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full appearance-none bg-transparent border-b border-text-primary/30 py-3 pr-10 pl-0 font-ui text-base uppercase text-text-primary focus:outline-none focus:border-text-primary transition-colors"
            >
              {allProducts.map((product) => (
                <option key={product} value={product}>
                  {product}
                </option>
              ))}
            </select>
            <ChevronRight
              aria-hidden="true"
              className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 rotate-90 text-text-primary/60"
            />
          </div>
        </div>
      )}

      <SectionWrapper background="white" padding="md" animate={false}>
        <div className="flex flex-col gap-12 lg:gap-24 w-full mx-auto">
          {filteredProjects.map((project, index) => (
            <FadeIn key={project.id} initialY={24} duration={0.7} className="relative">
              <DetailedProjectEntry project={project} layout={index % 2 === 0 ? 'left' : 'right'} sourceBlock="detailedProjectsOverview" />
            </FadeIn>
          ))}
        </div>
      </SectionWrapper>
    </div>
  )
}
